import React from 'react';
import { CompleteKundali } from '../../types/kundali';
import { getPlanetFullName } from '../../utils/language';
import { GranthaCommonProps } from './Grantha1_JanmaGanita';
import { calculateNumerologyProfile } from '../../utils/bhriguPatrikaCalculations';
import { AstrologerBranding } from '../../lib/astrologerBrandingService';
import { calculateTaraBala } from '../../utils/muhurtaEngine';
export const Grantha8_GemologyMuhurta: React.FC<GranthaCommonProps> = ({
  kundali,
  lang,
  isBw,
  pageBorder,
  runningHeaderBorder,
  headingPrimary,
  headingAccent,
  innerCardBg,
  tableBorder,
  tableHeaderBg,
  tableRowEven,
  tableRowOdd,
  mutedText,
  renderPageFooter,
  branding
}) => {
  const isHi = lang === 'hi' || lang === 'sa';
  const { birthDetails, planets = [], houses = [] } = kundali;
  const activeBranding: Partial<AstrologerBranding> = branding || {};
  const numerology = React.useMemo(() => calculateNumerologyProfile(birthDetails.date), [birthDetails.date]);

  const moonNakIdx = planets.find(p => p.name === 'Moon')?.nakshatraIndex ?? 0;
  // Key Siddhi Nakshatras: Pushya (7), Hasta (12)
  const pushyaTara = React.useMemo(() => calculateTaraBala(moonNakIdx, 7), [moonNakIdx]);
  const hastaTara = React.useMemo(() => calculateTaraBala(moonNakIdx, 12), [moonNakIdx]);

  // Identify Lagna, 5th, 9th Lords for gemstone prescription
  const lagnaLord = houses[0]?.signLord || 'Mars';
  const fifthLord = houses[4]?.signLord || 'Sun';
  const ninthLord = houses[8]?.signLord || 'Jupiter';

  const gemstoneData = [
    {
      role: isHi ? 'जीव रत्न (Life Stone • देह रक्षक)' : 'Life Stone (Lagna Lord • Vitality)',
      lord: lagnaLord,
      gem: lagnaLord === 'Sun' ? 'Ruby (माणिक्य)' : lagnaLord === 'Moon' ? 'Pearl (मोती)' : lagnaLord === 'Mars' ? 'Red Coral (मूँगा)' : lagnaLord === 'Mercury' ? 'Emerald (पन्ना)' : lagnaLord === 'Jupiter' ? 'Yellow Sapphire (पुखराज)' : lagnaLord === 'Venus' ? 'Diamond / White Zircon (हीरा)' : 'Blue Sapphire (नीलम)',
      metal: ['Sun', 'Mars', 'Jupiter'].includes(lagnaLord) ? 'Gold / Copper' : lagnaLord === 'Moon' ? 'Pure Silver' : lagnaLord === 'Mercury' ? 'Gold / Bronze' : 'Gold / Panchadhatu',
      finger: ['Sun', 'Mars'].includes(lagnaLord) ? 'Ring Finger (अनामिका)' : lagnaLord === 'Jupiter' ? 'Index Finger (तर्जनी)' : lagnaLord === 'Mercury' ? 'Little Finger (कनिष्ठिका)' : lagnaLord === 'Moon' ? 'Little Finger' : 'Middle Finger (मध्यमा)',
      day: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].indexOf(lagnaLord) >= 0 ? ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].indexOf(lagnaLord) : 0],
      mantra: `ॐ ह्रीं श्रीं ${getPlanetFullName(lagnaLord, 'sa')} नमः`,
      weight: '5.25 to 7.25 Ratti',
      uparatna: lagnaLord === 'Sun' ? 'Red Garnet' : lagnaLord === 'Moon' ? 'Moonstone' : lagnaLord === 'Mars' ? 'Carnelian' : lagnaLord === 'Mercury' ? 'Peridot / Green Tourmaline' : lagnaLord === 'Jupiter' ? 'Yellow Topaz / Citrine' : lagnaLord === 'Venus' ? 'White Opal' : 'Amethyst / Blue Topaz'
    },
    {
      role: isHi ? 'भाग्य रत्न (Lucky Stone • पंचमेश)' : 'Auspicious Stone (5th Lord • Intellect)',
      lord: fifthLord,
      gem: fifthLord === 'Sun' ? 'Ruby (माणिक्य)' : fifthLord === 'Moon' ? 'Pearl (मोती)' : fifthLord === 'Mars' ? 'Red Coral (मूँगा)' : fifthLord === 'Mercury' ? 'Emerald (पन्ना)' : fifthLord === 'Jupiter' ? 'Yellow Sapphire (पुखराज)' : fifthLord === 'Venus' ? 'Diamond / White Zircon (हीरा)' : 'Blue Sapphire (नीलम)',
      metal: ['Sun', 'Mars', 'Jupiter'].includes(fifthLord) ? 'Gold / Copper' : fifthLord === 'Moon' ? 'Pure Silver' : 'Gold / Panchadhatu',
      finger: ['Sun', 'Mars'].includes(fifthLord) ? 'Ring Finger (अनामिका)' : fifthLord === 'Jupiter' ? 'Index Finger (तर्जनी)' : fifthLord === 'Mercury' ? 'Little Finger (कनिष्ठिका)' : fifthLord === 'Moon' ? 'Little Finger' : 'Middle Finger (मध्यमा)',
      day: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].indexOf(fifthLord) >= 0 ? ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].indexOf(fifthLord) : 0],
      mantra: `ॐ ऐं क्लीं ${getPlanetFullName(fifthLord, 'sa')} नमः`,
      weight: '4.25 to 6.25 Ratti',
      uparatna: fifthLord === 'Sun' ? 'Red Garnet' : fifthLord === 'Moon' ? 'Moonstone' : fifthLord === 'Mars' ? 'Carnelian' : fifthLord === 'Mercury' ? 'Peridot' : fifthLord === 'Jupiter' ? 'Yellow Citrine' : fifthLord === 'Venus' ? 'White Opal' : 'Amethyst'
    },
    {
      role: isHi ? 'पुण्य रत्न (Fortune Stone • नवमेश)' : 'Fortune Stone (9th Lord • Divine Grace)',
      lord: ninthLord,
      gem: ninthLord === 'Sun' ? 'Ruby (माणिक्य)' : ninthLord === 'Moon' ? 'Pearl (मोती)' : ninthLord === 'Mars' ? 'Red Coral (मूँगा)' : ninthLord === 'Mercury' ? 'Emerald (पन्ना)' : ninthLord === 'Jupiter' ? 'Yellow Sapphire (पुखराज)' : ninthLord === 'Venus' ? 'Diamond / White Zircon (हीरा)' : 'Blue Sapphire (नीलम)',
      metal: ['Sun', 'Mars', 'Jupiter'].includes(ninthLord) ? 'Gold / Copper' : ninthLord === 'Moon' ? 'Pure Silver' : 'Gold / Panchadhatu',
      finger: ['Sun', 'Mars'].includes(ninthLord) ? 'Ring Finger (अनामिका)' : ninthLord === 'Jupiter' ? 'Index Finger (तर्जनी)' : ninthLord === 'Mercury' ? 'Little Finger (कनिष्ठिका)' : ninthLord === 'Moon' ? 'Little Finger' : 'Middle Finger (मध्यमा)',
      day: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].indexOf(ninthLord) >= 0 ? ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].indexOf(ninthLord) : 0],
      mantra: `ॐ ग्रां ग्रीं ग्रौं सः ${getPlanetFullName(ninthLord, 'sa')} नमः`,
      weight: '5.25 to 8.25 Ratti',
      uparatna: ninthLord === 'Sun' ? 'Red Spinel' : ninthLord === 'Moon' ? 'White Agate' : ninthLord === 'Mars' ? 'Red Jasper' : ninthLord === 'Mercury' ? 'Green Jade' : ninthLord === 'Jupiter' ? 'Yellow Beryl' : ninthLord === 'Venus' ? 'White Sapphire' : 'Blue Spinel'
    }
  ];

  const detectedOrClassicalYogas = React.useMemo(() => {
    if (kundali.yogas && kundali.yogas.length > 0) {
      return kundali.yogas.slice(0, 8).map(y => ({
        name: isHi ? (y.sanskritName || y.name) : y.name,
        sloka: y.shlokaReference || 'वृहत्पाराशरहोराशास्त्रे योगविवेकः',
        condition: `${y.category} • ${y.fruitionStatus.replace(/_/g, ' ')}`,
        result: isHi ? y.result.hi : y.result.en,
        status: y.fruitionStatus
      }));
    }
    return [];
  }, [kundali.yogas, isHi]);

  return (
    <>
      {/* ========================================================================= */}
      {/* PAGE 53: CONSECRATED GEMSTONES, METALS & PURIFICATION MANTRAS             */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ८ • प्राण-प्रतिष्ठित रत्न, धातु व मन्त्र' : 'Grantha 8 • Consecrated Gemology'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ५३' : 'Page 53'}</span>
        </div>

        <div className="space-y-3 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'प्राण-प्रतिष्ठित रत्न, धातु, उंगली, धारण मुहूर्त एवं वैदिक मन्त्र' : 'CONSECRATED GEMSTONES, METALS, FINGERS & VEDIC MANTRAS'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              {isHi
                ? 'लग्न (जीव), पंचम (भाग्य) एवं नवम (पुण्य) त्रिकोण स्वामियों के शुभ रत्न, शुद्धिकरण विधि व उप-रत्न।'
                : 'Trine lords (1st, 5th, 9th) natural gemstones, carat weight, metal alloys, consecration rituals and substitutes.'}
            </p>
          </div>

          {/* 3 Main Gemstones Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {gemstoneData.map((gd, idx) => (
              <div key={idx} className={`rounded-xl border p-3 space-y-1.5 ${innerCardBg}`}>
                <div className="border-b pb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100/70 px-1.5 py-0.5 rounded">
                    {gd.role}
                  </span>
                  <h3 className={`text-sm font-bold font-serif mt-1 ${headingPrimary}`}>{gd.gem}</h3>
                </div>
                <div className="space-y-1 text-xs">
                  <div><span className={mutedText}>Weight:</span> <strong className="font-mono">{gd.weight}</strong></div>
                  <div><span className={mutedText}>Metal:</span> <span className="font-medium">{gd.metal}</span></div>
                  <div><span className={mutedText}>Finger:</span> <span className="font-medium">{gd.finger}</span></div>
                  <div><span className={mutedText}>Muhurta:</span> <span className="font-medium text-emerald-800">{gd.day} Shukla Paksha</span></div>
                  <div><span className={mutedText}>Substitute:</span> <span className="font-medium text-gray-700">{gd.uparatna}</span></div>
                  <div className="pt-1 border-t text-[10px] font-mono text-astro-primary font-bold">{gd.mantra}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Purification & Cautions Matrix */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className={`rounded-xl border p-2.5 text-xs space-y-1 ${tableRowEven}`}>
              <h4 className={`font-bold font-serif text-xs ${headingPrimary}`}>
                {isHi ? 'रत्न प्राण-प्रतिष्ठा एवं शुद्धिकरण विधि' : 'Vedic Consecration & Purification Ritual'}
              </h4>
              <p className="text-[11px] text-gray-700 leading-relaxed">
                {isHi
                  ? 'रत्न को कच्चे दूध, गंगाजल, तुलसी दल, शहद व घी के पंचामृत में २ घण्टे रखें। तत्पश्चात सम्बद्ध ग्रह के १०८ वैदिक मन्त्रों का जप कर शुभ नक्षत्र वेला में धारण करें।'
                  : 'Immerse the gemstone in unboiled cow milk, Gangajal, holy basil, honey and ghee for 2 hours. Chant 108 japas of the planetary mantra during auspicious sunrise hora.'}
              </p>
            </div>
            <div className={`rounded-xl border p-2.5 text-xs space-y-1 bg-red-50/50 border-red-200`}>
              <h4 className="font-bold font-serif text-xs text-red-900">
                {isHi ? 'शास्त्रीय वर्जनाएं एवं सावधानियां' : 'Shastric Prohibitions & Cautions'}
              </h4>
              <p className="text-[11px] text-red-800 leading-relaxed">
                {isHi
                  ? 'षष्ठेश, अष्टमेश अथवा द्वादशेश का रत्न कभी धारण न करें। कभी भी नीलम (शनि) और माणिक्य (सूर्य) अथवा मोती और गोमेद को एक साथ एक ही हाथ में न पहनें।'
                  : 'Never wear gemstones belonging to functional malefic lords (6th, 8th, 12th). Never combine Sun\'s Ruby with Saturn\'s Sapphire or Moon\'s Pearl with Rahu\'s Hessonite.'}
              </p>
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ८ • पृष्ठ ५३' : 'Grantha 8 • Page 53')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 54: THE 8 CLASSICAL RAJA YOGAS & MAHAPURUSHA YOGAS                   */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ८ • अष्ट शास्त्रीय राजयोग संकलन' : 'Grantha 8 • 8 Classical Raja Yogas'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ५४' : 'Page 54'}</span>
        </div>

        <div className="space-y-3 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'अष्ट शास्त्रीय राजयोग, पंच महापुरुष योग एवं प्रमाण श्लोक' : 'THE 8 CLASSICAL RAJA YOGAS & MAHAPURUSHA FORMATIONS'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              {isHi
                ? 'वृहत्पाराशर होराशास्त्र एवं फलदीपिका सम्मत केन्द्र-त्रिकोण राजयोग तथा जातक कुण्डली में क्रियान्वयन।'
                : 'Parashari angular sovereign combinations, pancha-mahapurusha formations, authentic shlokas and manifest potency.'}
            </p>
          </div>

          {/* 8 Raja Yogas Grid or Honest Empty State */}
          {detectedOrClassicalYogas.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {detectedOrClassicalYogas.map((ry, idx) => (
                <div key={idx} className={`rounded-xl border p-2.5 space-y-1 ${innerCardBg}`}>
                  <div className="flex items-center justify-between border-b pb-1">
                    <h3 className={`font-bold font-serif text-xs ${headingPrimary}`}>{ry.name}</h3>
                    <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border ${
                      ry.status === 'HIGH_POTENCY' 
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                        : ry.status === 'AFFLICTED_OR_DORMANT'
                          ? 'bg-amber-50 text-amber-800 border-amber-200'
                          : 'bg-astro-bg text-astro-primary border-astro-border'
                    }`}>
                      {ry.condition}
                    </span>
                  </div>
                  <pre className={`text-[10px] font-serif font-bold whitespace-pre-line leading-tight ${headingPrimary}`}>
                    {ry.sloka}
                  </pre>
                  <p className={`text-[11px] text-gray-700 leading-tight pt-0.5 border-t`}>
                    {ry.result}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className={`rounded-xl border p-6 text-center space-y-2 ${innerCardBg}`}>
              <h3 className={`font-bold font-serif text-sm ${headingPrimary}`}>
                {isHi ? 'गणनाकृत कुण्डली में कोई विशेष राजयोग अथवा महापुरुष योग प्राप्त नहीं हुआ।' : 'No qualifying Raja or Mahapurusha Yogas detected in this chart.'}
              </h3>
              <p className={`text-xs ${mutedText}`}>
                {isHi
                  ? 'इस जन्मपत्रिका में ग्रह स्थिति सामान्य क्रम में है। कोई विशिष्ट केन्द्र-त्रिकोण राजयोग या पंचमहापुरुष योग के आवश्यक शास्त्रीय लक्षण पूर्ण नहीं हुए।'
                  : 'Planetary configurations follow general natal distributions; no angular sovereign combinations or Pancha Mahapurusha formations meet required classical thresholds.'}
              </p>
            </div>
          )}
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ८ • पृष्ठ ५४' : 'Grantha 8 • Page 54')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 55: MUHURTA, TITHI-VARA SYNERGY & VEDIC NUMEROLOGY                   */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ८ • मुहूर्त विचार एवं अंक ज्योतिष' : 'Grantha 8 • Muhurta & Numerology'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ५५' : 'Page 55'}</span>
        </div>

        <div className="space-y-3 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'शुभ मुहूर्त निर्देश, मांगलिक/कालसर्प परीक्षा एवं वैदिक अंक विचार' : 'AUSPICIOUS MUHURTA, MANGLIK DIAGNOSTICS & VEDIC NUMEROLOGY'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              {isHi
                ? 'वार-तिथि-नक्षत्र सिद्धि योग, मांगलिक अपवाद, राहु-केतु अक्ष एवं मूलांक-भाग्यांक तरंग दैर्ध्य।'
                : 'Tithi-Vara-Nakshatra synergy, Manglik/Kaalsarp exceptions, and Pythagorean-Vedic numerological vibrations.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Box A: Muhurta & Auspicious Timings */}
            <div className={`rounded-xl border p-3 space-y-1.5 ${innerCardBg}`}>
              <h3 className={`font-bold uppercase tracking-wider text-xs border-b pb-1 ${headingPrimary}`}>
                {isHi ? 'शुभ मुहूर्त, सिद्ध योग एवं जातक तारा-बल' : 'Auspicious Muhurta & Native Tara Resonances'}
              </h3>
              <div className="space-y-1.5 text-xs text-gray-700">
                <div className="flex justify-between border-b pb-0.5">
                  <span className={mutedText}>Amrit Siddhi:</span>
                  <span className="font-semibold text-emerald-800">
                    Sun+Hasta ({hastaTara.taraName}) • Thu+Pushya ({pushyaTara.taraName})
                  </span>
                </div>
                <div className="flex justify-between border-b pb-0.5">
                  <span className={mutedText}>Sarvartha Siddhi:</span>
                  <span className="font-semibold text-emerald-800">
                    Wed+Rohini • Fri+Revati (Universal Fruition)
                  </span>
                </div>
                <div className="flex justify-between border-b pb-0.5">
                  <span className={mutedText}>Rahu Kaal Precaution:</span>
                  <span className="font-semibold text-red-800">Avoid enterprise starts during Rahu window</span>
                </div>
                <div className="flex justify-between pb-0.5">
                  <span className={mutedText}>Abhijit Muhurta:</span>
                  <span className="font-semibold text-astro-primary">Mid-day 48-minute universal victory window</span>
                </div>
              </div>
            </div>

            {/* Box B: Manglik & Kaalsarp Audit */}
            <div className={`rounded-xl border p-3 space-y-1.5 ${innerCardBg}`}>
              <h3 className={`font-bold uppercase tracking-wider text-xs border-b pb-1 ${headingPrimary}`}>
                {isHi ? 'मांगलिक दोष एवं कालसर्प योग परीक्षा' : 'Manglik & Kaalsarp Audit'}
              </h3>
              <div className="space-y-1.5 text-xs text-gray-700">
                <div className="p-1 rounded bg-white/70 border">
                  <span className="font-bold text-astro-primary">{isHi ? 'मांगलिक स्थिति: ' : 'Manglik Status: '}</span>
                  <span>
                    {kundali.manglik?.isManglik
                      ? kundali.manglik.isEffectivelyMitigated || (kundali.manglik.cancellations && kundali.manglik.cancellations.length > 0)
                        ? (isHi ? 'दोष परिहार सक्रिय — ' : 'Mitigated — ') + (kundali.manglik.summary || kundali.manglik.cancellations?.[0] || '')
                        : (isHi ? `सक्रिय दोष (${kundali.manglik.severityPercent}%) — ` : `Active (${kundali.manglik.severityPercent}%) — `) + (kundali.manglik.summary || '')
                      : (isHi ? 'दोष मुक्त — कुण्डली में कोई मांगलिक प्रभाव उपस्थित नहीं है।' : 'Free from Manglik — No Kuja Dosha detected across Lagna, Moon, or Venus.')}
                  </span>
                </div>
                <div className="p-1 rounded bg-white/70 border">
                  <span className="font-bold text-astro-primary">{isHi ? 'कालसर्प परीक्षा: ' : 'Kaalsarp Audit: '}</span>
                  <span>
                    {kundali.kaalSarp?.hasKaalSarp
                      ? `${kundali.kaalSarp.type || 'Kaal Sarp'}: ${kundali.kaalSarp.description}`
                      : kundali.kaalSarp?.isPartial
                        ? `${isHi ? 'आंशिक कालसर्प / भंग' : 'Partial Kaalsarp'}: ${kundali.kaalSarp.description}`
                        : (isHi ? 'पूर्णतः निर्दोष — ग्रह राहु-केतु अक्ष के दोनों ओर संतुलित रूप से वितरित हैं।' : 'Completely Free — Planetary positions distributed naturally across both hemispheres.')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Vedic Numerology Strip */}
          <div className={`rounded-xl border p-3 space-y-2 ${tableRowEven}`}>
            <h3 className={`font-bold uppercase tracking-wider text-xs border-b pb-1 ${headingPrimary}`}>
              {isHi ? 'वैदिक अंक ज्योतिष समन्वय (Vedic Numerology Profile)' : 'Vedic Numerology & Swarodaya Vibrations'}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="p-2 rounded bg-white/80 border text-center">
                <div className={mutedText}>Radical (मूलांक)</div>
                <div className="text-lg font-bold font-mono text-astro-primary">{numerology.moolank}</div>
                <div className="text-[10px] text-gray-600">{numerology.auspiciousPlanets[0] || 'Sun'} Lord</div>
              </div>
              <div className="p-2 rounded bg-white/80 border text-center">
                <div className={mutedText}>Destiny (भाग्यांक)</div>
                <div className="text-lg font-bold font-mono text-indigo-800">{numerology.bhagyank}</div>
                <div className="text-[10px] text-gray-600">Soul Purpose</div>
              </div>
              <div className="p-2 rounded bg-white/80 border text-center">
                <div className={mutedText}>Auspicious Gem</div>
                <div className="text-sm font-bold font-serif text-emerald-800 mt-1">{numerology.auspiciousGem}</div>
                <div className="text-[10px] text-gray-600">{numerology.uparatna}</div>
              </div>
              <div className="p-2 rounded bg-white/80 border text-center">
                <div className={mutedText}>Auspicious Vector</div>
                <div className="text-xs font-bold text-amber-900 mt-1">{(numerology.auspiciousDays || []).slice(0, 2).join(', ')}</div>
                <div className="text-[10px] text-gray-600">{numerology.auspiciousColor} • {numerology.auspiciousDirection}</div>
              </div>
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ८ • पृष्ठ ५५' : 'Grantha 8 • Page 55')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 56: DAIVAJNA ASHIRVADA, COLOPHON & CERTIFIED VERIFICATION SEAL       */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ८ • दैवज्ञ आशीर्वाद, ग्रन्थ सूची एवं प्रमाण पत्र' : 'Grantha 8 • Colophon & Verification'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ५६' : 'Page 56'}</span>
        </div>

        <div className="space-y-4 my-auto text-center">
          <div className="space-y-1 border-b pb-2">
            <h2 className={`text-lg sm:text-xl font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'दैवज्ञ आशीर्वचन, शास्त्रीय ग्रन्थ सूची एवं प्रामाणिक मुहर' : 'DAIVAJNA ASHIRVADA, SHASTRIC COLOPHON & CERTIFICATION SEAL'}
            </h2>
            <p className={`text-xs ${mutedText}`}>
              {isHi
                ? 'सम्पूर्णा भृगु महा-संहिता ५६ पृष्ठ महा-ग्रन्थ समापन एवं दैवज्ञ प्रमाण पत्र।'
                : 'Final epilogue, shastric lineage references, certified signature seal and cosmic invocation.'}
            </p>
          </div>

          {/* Mangala Shloka Box */}
          <div className={`max-w-xl mx-auto p-4 rounded-2xl border space-y-2 text-center ${innerCardBg}`}>
            <div className={`text-[10px] uppercase font-bold tracking-widest ${headingAccent}`}>
              — सम्पूर्ण भृगु महा-संहिता महा-मङ्गल आशीर्वाद —
            </div>
            <pre className={`text-xs sm:text-sm font-serif font-bold whitespace-pre-line leading-relaxed ${headingPrimary}`}>
              स्वस्ति प्रजाभ्यः परिपालयन्तां न्यायेन मार्गेण महीं महीशाः।{'\n'}
              गोब्राह्मणेभ्यः शुभमस्तु नित्यं लोकाः समस्ताः सुखिनो भवन्तु॥{'\n'}
              ॐ शान्तिः शान्तिः शान्तिः॥
            </pre>
            <p className={`text-xs italic pt-1 border-t leading-normal ${mutedText}`}>
              {isHi
                ? 'समस्त प्रजा, गो, विप्र एवं सम्पूर्ण सृष्टि का कल्याण हो। जातक का जीवन धर्म, अर्थ, काम एवं मोक्ष चतुर्विध पुरुषार्थ से परिपूर्ण रहे।'
                : 'May all beings prosper through righteousness. May divine light illuminate the native\'s path toward dharma, wealth, fulfillment and spiritual liberation.'}
            </p>
          </div>

          {/* Shastric Colophon Grid */}
          <div className={`max-w-xl mx-auto rounded-xl border p-3 text-left space-y-1.5 ${tableRowEven}`}>
            <h3 className={`font-bold font-serif text-xs uppercase tracking-wider ${headingPrimary}`}>
              {isHi ? 'प्रामाणिक शास्त्रीय आधार ग्रन्थ सूची (Canonical Shastric Citations)' : 'Canonical Shastric Foundations'}
            </h3>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-gray-700">
              <div>• वृहत्पाराशर होराशास्त्र (Maharishi Parashara)</div>
              <div>• फलदीपिका (Mantreshwara)</div>
              <div>• सारावली (Kalyana Varma)</div>
              <div>• जातक पारिजात (Vaidyanatha Dikshita)</div>
              <div>• जैमिनी उपदेश सूत्र (Maharishi Jaimini)</div>
              <div>• लाल किताब (१९५२ साहिब फरमान)</div>
              <div>• मानसागरी (Manasagara Haragovinda)</div>
              <div>• कृष्णमूर्ति पद्धति (Prof. K.S. Krishnamurti)</div>
            </div>
          </div>

          {/* Certification Verification Seal & Signature Block */}
          <div className="max-w-md mx-auto pt-2">
            <div className={`rounded-2xl border-2 p-4 text-center space-y-2 ${isBw ? 'bg-white border-black' : 'bg-gradient-to-b from-[#FFFDF7] to-[#F7F3EA] border-[#174C4B] shadow-sm'}`}>
              <div className={`text-xs uppercase font-bold tracking-widest ${headingAccent}`}>
                CERTIFIED VEDIC HOROSCOPE COMPENDIUM
              </div>
              <div className="text-xs text-gray-600 font-mono">
                Document Unique ID: BHRIGU-56-KUNDALI-{birthDetails.name?.replace(/\s+/g, '').toUpperCase() || 'VEDA'}
              </div>
              <div className="flex items-center justify-around pt-3 border-t">
                <div className="text-center">
                  <div className={`text-xs font-bold font-serif ${headingPrimary}`}>
                    {activeBranding.name || 'Acharya Daivajna'}
                  </div>
                  <div className={`text-[10px] ${mutedText}`}>
                    {activeBranding.title || 'Vedic Astrologer & Jyotishacharya'}
                  </div>
                </div>
                <div className="w-16 h-16 rounded-full border-2 border-dashed border-amber-600/70 flex items-center justify-center p-1">
                  <span className="text-[9px] font-bold text-amber-800 text-center leading-tight uppercase font-mono">
                    VERIFIED<br />VEDIC<br />SEAL
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ८ • पृष्ठ ५६' : 'Grantha 8 • Page 56')}
      </div>
    </>
  );
};
